<template>
  <div class="h-[280px] relative">
    <Pie :data="chartData" :options="chartOptions" />
  </div>
</template>

<script setup lang="ts">
import { computed, shallowRef, defineProps } from 'vue'
import { Pie } from 'vue-chartjs'
import { useAccountStore } from '@/stores/account'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'

ChartJS.register(ArcElement, Tooltip, Legend)

const props = defineProps({
  startDate: {
    type: Date,
    required: true
  },
  endDate: {
    type: Date,
    required: true
  }
})

const accountStore = useAccountStore()

const chartOptions = shallowRef({
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 500
  },
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        usePointStyle: true,
        padding: 16,
        boxWidth: 6,
        boxHeight: 6,
        font: {
          size: 11
        }
      }
    },
    title: {
      display: true,
      text: 'Status das Contas',
      font: {
        size: 14,
        weight: 'bold'
      },
      padding: {
        bottom: 15
      }
    },
    tooltip: {
      callbacks: {
        label: (context: any) => {
          const label = context.label || ''
          const value = context.raw || 0
          const dataset = context.dataset
          const total = dataset.data.reduce((acc: number, curr: number) => acc + curr, 0)
          const percentage = ((value / total) * 100).toFixed(1)
          return `${label}: ${value} conta${value !== 1 ? 's' : ''} (${percentage}%)`
        }
      }
    }
  }
})

const statusConfig = shallowRef({
  'PENDENTE': {
    label: 'Pendentes',
    color: '#FFCE56',
    order: 1
  },
  'AGENDADO': {
    label: 'Agendadas',
    color: '#36A2EB',
    order: 2
  },
  'PAGO': {
    label: 'Pagas',
    color: '#4BC0C0',
    order: 3
  },
  'ATRASADO': {
    label: 'Atrasadas',
    color: '#FF6384',
    order: 4
  },
  'CANCELADO': {
    label: 'Canceladas',
    color: '#9966FF',
    order: 5
  }
})

function determineAccountStatus(account: any) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const dueDate = new Date(account.dueDate)
  dueDate.setHours(0, 0, 0, 0)
  
  const scheduledDate = account.scheduledDate ? new Date(account.scheduledDate) : null
  if (scheduledDate) scheduledDate.setHours(0, 0, 0, 0)
  
  // Se a conta está cancelada
  if (account.status === 'CANCELADO') return 'CANCELADO'
  
  // Se tem data agendada
  if (scheduledDate) {
    // Se a data agendada já passou, conta está paga
    if (scheduledDate < today) {
      return 'PAGO'
    }
    // Se a data agendada é hoje ou futura, conta está agendada
    return 'AGENDADO'
  }
  
  // Se não tem data agendada, verifica o vencimento
  if (dueDate < today) {
    return 'ATRASADO'
  }
  
  // Se a data de vencimento é hoje ou futura, está pendente
  return 'PENDENTE'
}

const accountsByStatus = computed(() => {
  // Separar contas por tipo (payable/receivable) e status
  const counts = {
    payable: Object.keys(statusConfig.value).reduce((acc, status) => {
      acc[status] = accountStore.accounts
        .filter(account => {
          const dueDate = new Date(account.dueDate)
          return account.type === 'payable' && 
            determineAccountStatus(account) === status &&
            dueDate >= props.startDate && 
            dueDate <= props.endDate
        })
        .length
      return acc
    }, {} as Record<string, number>),
    
    receivable: Object.keys(statusConfig.value).reduce((acc, status) => {
      acc[status] = accountStore.accounts
        .filter(account => {
          const dueDate = new Date(account.dueDate)
          return account.type === 'receivable' && 
            determineAccountStatus(account) === status &&
            dueDate >= props.startDate && 
            dueDate <= props.endDate
        })
        .length
      return acc
    }, {} as Record<string, number>)
  }

  return counts
})

const chartData = computed(() => {
  const payableData = accountsByStatus.value.payable
  const receivableData = accountsByStatus.value.receivable

  return {
    labels: Object.values(statusConfig.value).map(config => config.label),
    datasets: [
      {
        label: 'Contas a Pagar',
        data: Object.entries(statusConfig.value)
          .sort((a, b) => a[1].order - b[1].order)
          .map(([status]) => payableData[status]),
        backgroundColor: Object.values(statusConfig.value)
          .sort((a, b) => a.order - b.order)
          .map(config => config.color),
        borderWidth: 0
      },
      {
        label: 'Contas a Receber',
        data: Object.entries(statusConfig.value)
          .sort((a, b) => a[1].order - b[1].order)
          .map(([status]) => receivableData[status]),
        backgroundColor: Object.values(statusConfig.value)
          .sort((a, b) => a.order - b.order)
          .map(config => `${config.color}80`), // Versão mais clara das cores
        borderWidth: 0
      }
    ]
  }
})
</script>
